import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  MapPin,
  Phone,
  Globe,
  Clock,
  Star,
  Building2,
  CheckCircle,
  Shield,
  ExternalLink,
  Youtube,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  FileText,
  Camera,
  Mail,
  CreditCard,
  Utensils,
  ShoppingBag,
  Briefcase,
  Heart,
  Car,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { platformApi } from '@/services/platformApi';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import { i18n } from '@/utils/i18n';
import { getPaymentAppBaseUrl } from '@/utils/appConfig';
import { normalizeApiEntityId } from '@/utils/normalizeApiEntityId';
import type { MerchantProfile } from '@/types/api/merchant';
import { useAuth } from '@/context/AuthContext';
import { useMerchantStorefrontGallery } from '@/hooks/useMerchantStorefrontGallery';
import { useMerchantProfilePhotoPoll } from '@/hooks/useMerchantProfilePhotoPoll';
import type { MarketplaceReturnState } from '@/pages/marketplace/marketplaceNavigation';
import { merchantProfileAwaitingPhotoHydration } from '@/utils/merchantStorefrontGallery';
import MerchantStorefrontPhotoGallery from '@/components/merchant/MerchantStorefrontPhotoGallery';
import MerchantStorefrontCatalog from '@/components/merchant/MerchantStorefrontCatalog';

const isMerchantRoleEntry = (p: { role?: string } | null | undefined) =>
  String(p?.role ?? '')
    .toUpperCase()
    .replace(/\s+/g, '') === 'MERCHANT';

function isPublicMerchantNotFoundError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return msg === 'MERCHANT_PROFILE_HTTP_404' || msg.includes('MERCHANT_PROFILE_HTTP_404');
}

/** Missing / unpublished storefront or wrong id — not an app bug; avoid noisy error logging. */
function isBenignMerchantUnavailableError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg === 'MERCHANT_PROFILE_HTTP_404' || msg.includes('MERCHANT_PROFILE_HTTP_404')) return true;
  if (/failed to fetch public user profile:\s*404/i.test(msg)) return true;
  if (msg.includes('USER_PROFILE_HTML_RESPONSE') || msg.includes('USER_PROFILE_INVALID_JSON')) return true;
  if (msg.includes('USER_PROFILE_EMPTY_BODY')) return true;
  if (/\b404\b/.test(msg) && /merchant|user profile|payee|not found/i.test(msg)) return true;
  return false;
}

/** Profile responses may use _id or id */
function authUserCanonicalId(user: { _id?: unknown; id?: unknown } | null | undefined): string {
  if (!user) return '';
  return normalizeApiEntityId(user._id ?? user.id);
}

function storefrontIdsMatch(routeId: string, viewerId: string): boolean {
  if (!routeId || !viewerId) return false;
  if (routeId === viewerId) return true;
  if (/^[0-9a-f]{24}$/i.test(routeId) && /^[0-9a-f]{24}$/i.test(viewerId)) {
    return routeId.toLowerCase() === viewerId.toLowerCase();
  }
  return false;
}

type ProfileLoadError =
  | { kind: 'no_id' }
  | { kind: 'store_unavailable' }
  | { kind: 'failed'; detail: string };

const getBusinessTypeIcon = (type?: string) => {
  const iconClass = "w-8 h-8";
  switch (type?.toLowerCase()) {
    case 'restaurant':
    case 'cafe':
    case 'food':
      return <Utensils className={iconClass} />;
    case 'retail':
    case 'shop':
      return <ShoppingBag className={iconClass} />;
    case 'service':
    case 'professional':
      return <Briefcase className={iconClass} />;
    case 'health':
    case 'medical':
      return <Heart className={iconClass} />;
    case 'automotive':
      return <Car className={iconClass} />;
    default:
      return <Building2 className={iconClass} />;
  }
};

const formatHours = (hours: { open: string; close: string } | null) => {
  if (!hours || !hours.open || !hours.close) return i18n.__('merchantPublicProfile.closed');
  return `${hours.open} - ${hours.close}`;
};

interface MerchantPublicProfileProps {
  previewMode?: boolean;
  previewData?: MerchantProfile; // Use the new interface
}

const MerchantPublicProfile: React.FC<MerchantPublicProfileProps> = ({ 
  previewMode = false, 
  previewData 
}) => {
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(previewData || null);
  const [loading, setLoading] = useState(!previewMode);
  const [loadError, setLoadError] = useState<ProfileLoadError | null>(null);
  const [ownerPrivatePreview, setOwnerPrivatePreview] = useState(false);
  const { galleryImages } = useMerchantStorefrontGallery(merchant);

  const payeeIdForLinks =
    normalizeApiEntityId(merchant?.id) || normalizeApiEntityId(merchantId) || '';

  const handleHydratedProfile = useCallback((profile: MerchantProfile) => {
    setMerchant(profile);
  }, []);

  const isPollingPhotos = useMerchantProfilePhotoPoll(
    merchant,
    payeeIdForLinks || undefined,
    handleHydratedProfile,
    !previewMode &&
      !loadError &&
      !!merchant &&
      galleryImages.length === 0 &&
      merchantProfileAwaitingPhotoHydration(merchant)
  );

  const marketplaceReturn = (location.state as { marketplaceReturn?: MarketplaceReturnState })
    ?.marketplaceReturn;

  useEffect(() => {
    const loadMerchantProfile = async () => {
      // Skip loading if in preview mode
      if (previewMode && previewData) {
        setMerchant(previewData);
        setOwnerPrivatePreview(false);
        setLoading(false);
        return;
      }
      
      const routeId = normalizeApiEntityId(merchantId);
      if (!routeId) {
        setLoadError({ kind: 'no_id' });
        setOwnerPrivatePreview(false);
        setLoading(false);
        return;
      }

      const viewerId = authUserCanonicalId(user);
      const isStorefrontOwner =
        !!user && !user.isGuest && storefrontIdsMatch(routeId, viewerId);

      const tryOwnerPrivateProfile = async (): Promise<boolean> => {
        if (!isStorefrontOwner) return false;
        try {
          const priv = await platformApi.getMerchantProfile();
          setMerchant(priv);
          setOwnerPrivatePreview(true);
          if (import.meta.env.DEV) {
            console.debug(
              '[MerchantPublicProfile] Loaded authenticated merchant profile for storefront owner (private preview)',
              routeId
            );
          }
          return true;
        } catch (e) {
          if (import.meta.env.DEV) {
            console.debug('[MerchantPublicProfile] Owner private profile fetch failed', e);
          }
          return false;
        }
      };

      let holdLoadingForPendingAuth = false;

      const bailPendingAuth = (): boolean => {
        if (authLoading) {
          holdLoadingForPendingAuth = true;
          setLoading(true);
          return true;
        }
        return false;
      };

      try {
        setLoading(true);
        setLoadError(null);
        setOwnerPrivatePreview(false);

        if (!authLoading && isStorefrontOwner && platformApi.isAuthenticated()) {
          try {
            const pub = await platformApi.merchant.getPublicMerchantProfile(routeId);
            setMerchant(pub);
            setOwnerPrivatePreview(false);
            return;
          } catch (e) {
            if (import.meta.env.DEV) {
              console.debug(
                '[MerchantPublicProfile] Owner GET /public/merchant (with session) failed; trying /merchant/profile',
                e
              );
            }
          }
          try {
            const priv = await platformApi.getMerchantProfile();
            setMerchant(priv);
            setOwnerPrivatePreview(true);
            return;
          } catch (e) {
            if (import.meta.env.DEV) {
              console.debug(
                '[MerchantPublicProfile] Owner GET /merchant/profile failed; falling back to anonymous public flow',
                e
              );
            }
          }
        }

        try {
          const response = await platformApi.merchant.getPublicMerchantProfile(routeId);
          setMerchant(response);
          setOwnerPrivatePreview(false);
          return;
        } catch (first: unknown) {
          if (!isPublicMerchantNotFoundError(first)) {
            throw first;
          }
          if (import.meta.env.DEV) {
            console.debug(
              '[MerchantPublicProfile] GET /public/merchant/:userId 404; trying public user profile',
              routeId
            );
          }
        }

        const publicUser = await platformApi.user.getPublicUserProfile(routeId);
        const resolvedUserId = normalizeApiEntityId(publicUser.id);

        if (resolvedUserId && resolvedUserId !== routeId) {
          try {
            const again = await platformApi.merchant.getPublicMerchantProfile(resolvedUserId);
            setMerchant(again);
            setOwnerPrivatePreview(false);
            return;
          } catch {
            /* fall through to nested profile */
          }
        }

        const merchantEntry = publicUser.public?.otherProfiles?.find(isMerchantRoleEntry);
        const nested = merchantEntry?.profile;
        if (!nested || typeof nested !== 'object') {
          if (bailPendingAuth()) return;
          if (await tryOwnerPrivateProfile()) return;
          setLoadError({ kind: 'store_unavailable' });
          return;
        }

        const withId = {
          ...(nested as Record<string, unknown>),
          id:
            normalizeApiEntityId((nested as { id?: unknown }).id) ||
            resolvedUserId ||
            routeId,
        };
        const mapped = platformApi.merchant.mapPublicMerchantApiPayload(withId);
        setMerchant(mapped);
        setOwnerPrivatePreview(false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (isBenignMerchantUnavailableError(err)) {
          if (import.meta.env.DEV) {
            console.debug('[MerchantPublicProfile] Store not available (404 or no public profile):', message);
          }
          if (bailPendingAuth()) return;
          if (await tryOwnerPrivateProfile()) return;
          setLoadError({ kind: 'store_unavailable' });
        } else {
          console.error('Failed to load merchant profile:', message, err);
          setLoadError({
            kind: 'failed',
            detail: message || i18n.__('merchantPublicProfile.loadingError'),
          });
        }
      } finally {
        if (!holdLoadingForPendingAuth) {
          setLoading(false);
        }
      }
    };

    loadMerchantProfile();
  }, [
    merchantId,
    previewMode,
    previewData,
    user?._id,
    (user as { id?: unknown } | null | undefined)?.id,
    user?.isGuest,
    authLoading,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" message={i18n.__('merchantPublicProfile.loading')} />
      </div>
    );
  }

  if (loadError) {
    const isUnavailable = loadError.kind === 'store_unavailable';
    const title =
      loadError.kind === 'no_id'
        ? i18n.__('merchantPublicProfile.noId')
        : isUnavailable
          ? i18n.__('merchantPublicProfile.storeNotFound')
          : i18n.__('merchantPublicProfile.loadingError');
    const message =
      loadError.kind === 'no_id'
        ? i18n.__('merchantPublicProfile.noIdHint')
        : isUnavailable
          ? i18n.__('merchantPublicProfile.notFoundHelp')
          : loadError.kind === 'failed'
            ? loadError.detail
            : '';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage title={title} message={message} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <Alert>
              <AlertDescription>
                {i18n.__('merchantPublicProfile.notFound')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayBusinessName =
    (typeof merchant.businessName === 'string' ? merchant.businessName.trim() : '') ||
    payeeIdForLinks ||
    'Store';

  const handleMarketplaceBack = () => {
    if (!marketplaceReturn) return;
    navigate(marketplaceReturn.returnPath, {
      state: { restoreMarketplace: marketplaceReturn.restore },
    });
  };

  const routeIdForOwnerUi = normalizeApiEntityId(merchantId);
  const viewerIdForOwnerUi = authUserCanonicalId(user);
  const isStorefrontOwnerUi =
    !!user &&
    !user.isGuest &&
    storefrontIdsMatch(routeIdForOwnerUi, viewerIdForOwnerUi) &&
    !previewMode;
  const showOwnerUnpublishedBanner =
    isStorefrontOwnerUi && (ownerPrivatePreview || merchant.isPublished === false);

  const hasStructuredAddress =
    !!merchant.addressComponents &&
    typeof merchant.addressComponents === 'object' &&
    !('$concat' in merchant.addressComponents) &&
    !!(
      merchant.addressComponents.street ||
      merchant.addressComponents.city ||
      merchant.addressComponents.state ||
      merchant.addressComponents.postalCode
    );

  const hasFlatAddress =
    !!merchant.address &&
    typeof merchant.address === 'object' &&
    !!(
      merchant.address.street ||
      merchant.address.city ||
      merchant.address.state ||
      merchant.address.zipCode ||
      merchant.address.country
    );

  const socialLinks = [
    { platform: 'youtube', icon: Youtube, label: 'YouTube', color: 'hover:text-red-600' },
    { platform: 'instagram', icon: Instagram, label: 'Instagram', color: 'hover:text-pink-600' },
    { platform: 'facebook', icon: Facebook, label: 'Facebook', color: 'hover:text-blue-600' },
    { platform: 'tiktok', icon: Camera, label: 'TikTok', color: 'hover:text-black' },
    { platform: 'twitter', icon: Twitter, label: 'Twitter', color: 'hover:text-blue-400' },
    { platform: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: 'hover:text-blue-700' },
    { platform: 'wordpress', icon: FileText, label: 'Blog', color: 'hover:text-blue-500' },
  ];

  // Check for debug mode in URL
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('debug') === 'true';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {marketplaceReturn && (
          <div className="mb-4">
            <Button variant="ghost" size="sm" onClick={handleMarketplaceBack} className="px-0">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {marketplaceReturn.hadSearch
                ? i18n.__('merchantPublicProfile.backToSearchResults')
                : i18n.__('merchantPublicProfile.backToMarketplace')}
            </Button>
          </div>
        )}
        {showOwnerUnpublishedBanner && (
          <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertDescription className="space-y-1">
              <p className="font-medium">{i18n.__('merchantPublicProfile.ownerPreviewTitle')}</p>
              <p className="text-sm opacity-90">{i18n.__('merchantPublicProfile.ownerPreviewBody')}</p>
            </AlertDescription>
          </Alert>
        )}
        {/* Debug Info */}
        {debugMode && (
          <Card className="mb-4 p-4 bg-yellow-50 border-yellow-200">
            <h3 className="font-bold mb-2">Debug Info:</h3>
            <pre className="text-xs overflow-auto">
              {JSON.stringify({
                merchantId,
                hasData: !!merchant,
                businessName: merchant?.businessName || 'NOT SET',
                isPublished: merchant?.verification?.isVerified || false,
                completedOnboarding: merchant?.completedOnboarding || false,
                hasAddress: !!merchant?.addressComponents,
                hasPhone: !!merchant?.phone,
                hasEmail: !!merchant?.email,
                hasGoogleMapsEmbedUrl: !!merchant?.googleMapsEmbedUrl,
                googlePlaceId: merchant?.googlePlaceId || 'NOT SET',
                dataKeys: merchant ? Object.keys(merchant) : []
              }, null, 2)}
            </pre>
          </Card>
        )}
        
        {/* Store profile header */}
        <Card className="mb-8 overflow-hidden">
          {/* Full-width Pay Button Section */}
          <div className="p-4 bg-white border-b">
            <Button
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4"
              onClick={() => {
                if (!payeeIdForLinks) return;
                window.location.href = `${getPaymentAppBaseUrl()}/pay?payeeId=${encodeURIComponent(payeeIdForLinks)}`;
              }}
              disabled={!payeeIdForLinks}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              {i18n.__('merchantPublicProfile.pay').replace('{name}', displayBusinessName)}
            </Button>
          </div>

          <CardHeader className="pb-4">
            <div className="flex items-start gap-4">
              {/* Logo or Icon */}
              <div className="flex-shrink-0">
                {merchant.logo ? (
                  <img
                    src={merchant.logo}
                    alt={displayBusinessName}
                    className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                    {getBusinessTypeIcon(merchant.businessType)}
                  </div>
                )}
              </div>

              {/* Business Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      {/* Business Icon/Logo inline with name */}
                      {merchant.logo ? (
                        <img
                          src={merchant.logo}
                          alt={displayBusinessName}
                          className="w-8 h-8 rounded object-cover"
                        />
                      ) : (
                        <div className="text-gray-600">
                          {getBusinessTypeIcon(merchant.businessType)}
                        </div>
                      )}
                      <h1 className="text-2xl font-bold text-gray-900">
                        {displayBusinessName}
                      </h1>
                    </div>
                    {merchant.businessType && (() => {
                      const businessTypeStr = typeof merchant.businessType === 'string' 
                        ? merchant.businessType 
                        : (typeof merchant.businessType === 'object' && merchant.businessType !== null
                          ? null // Skip objects (MongoDB aggregation objects)
                          : String(merchant.businessType || ''));
                      return businessTypeStr ? (
                        <Badge variant="secondary" className="mt-1">
                          {businessTypeStr}
                        </Badge>
                      ) : null;
                    })()}
                  </div>

                  {/* Trust Badges */}
                  <div className="flex items-center gap-2">
                    {merchant.verification?.isVerified && (
                      <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        <span>{i18n.__('merchantPublicProfile.verified')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {merchant.description && (
                  <p className="text-gray-600 mt-2">
                    {merchant.description}
                  </p>
                )}

                {/* Google Maps Link */}
                {merchant.googleMapsUrl && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="default"
                      onClick={() => window.open(merchant.googleMapsUrl, '_blank')}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      {i18n.__('merchantPublicProfile.getDirections')}
                    </Button>
                  </div>
                )}

              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <Separator className="mb-4" />

            <div className="grid md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">{i18n.__('merchantPublicProfile.contactInfo')}</h3>
                <div className="space-y-2">
                  {merchant.phone && (() => {
                    const phoneStr = typeof merchant.phone === 'string' ? merchant.phone : String(merchant.phone || '');
                    return phoneStr && phoneStr !== '[object Object]' ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${phoneStr}`} className="hover:text-blue-600">
                          {phoneStr}
                        </a>
                      </div>
                    ) : null;
                  })()}
                  
                  {merchant.email && (() => {
                    const emailStr = typeof merchant.email === 'string' ? merchant.email : String(merchant.email || '');
                    return emailStr && emailStr !== '[object Object]' ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${emailStr}`} className="hover:text-blue-600">
                          {emailStr}
                        </a>
                      </div>
                    ) : null;
                  })()}
                  
                  {merchant.website && (() => {
                    const websiteStr = typeof merchant.website === 'string' ? merchant.website : String(merchant.website || '');
                    return websiteStr && websiteStr !== '[object Object]' && !websiteStr.startsWith('[') ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Globe className="w-4 h-4" />
                        <a 
                          href={websiteStr.startsWith('http') ? websiteStr : `https://${websiteStr}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 flex items-center gap-1"
                        >
                          {websiteStr.replace(/^https?:\/\//, '')}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ) : null;
                  })()}
                  
                  {hasStructuredAddress &&
                    (() => {
                      const isValidString = (value: unknown): boolean =>
                        value !== null &&
                        value !== undefined &&
                        typeof value === 'string' &&
                        value.trim() !== '';

                      const address = merchant.addressComponents!;
                      if (
                        address &&
                        typeof address === 'object' &&
                        ('$concat' in address ||
                          '$add' in address ||
                          '$multiply' in address ||
                          '$lookup' in address)
                      ) {
                        return null;
                      }

                      const street = isValidString(address?.street) ? address.street : '';
                      const city = isValidString(address?.city) ? address.city : '';
                      const state = isValidString(address?.state) ? address.state : '';
                      const postalCode = isValidString(address?.postalCode) ? address.postalCode : '';

                      if (!street && !city && !state && !postalCode) {
                        return null;
                      }

                      return (
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 mt-0.5" />
                          <div>
                            {street && <div>{street}</div>}
                            {(city || state || postalCode) && (
                              <div>{[city, state, postalCode].filter(Boolean).join(', ')}</div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  {hasFlatAddress && !hasStructuredAddress && (
                    <div className="flex items-start gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      <div>
                        {merchant.address!.street ? <div>{merchant.address!.street}</div> : null}
                        <div>
                          {[
                            merchant.address!.city,
                            merchant.address!.state,
                            merchant.address!.zipCode,
                            merchant.address!.country,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show message if no contact info available */}
                  {!merchant.phone &&
                    !merchant.email &&
                    !merchant.website &&
                    !hasStructuredAddress &&
                    !hasFlatAddress && (
                    <p className="text-sm text-gray-500 italic">
                      {i18n.__('merchantPublicProfile.noContactInfo')}
                    </p>
                  )}
                </div>

                {/* Social Media Links */}
                {merchant.socialMedia && Object.keys(merchant.socialMedia).length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">{i18n.__('merchantPublicProfile.followUs')}</h4>
                    <div className="flex gap-3">
                      {socialLinks.map(({ platform, icon: Icon, label, color }) => {
                        const url = merchant.socialMedia?.[platform as keyof typeof merchant.socialMedia];
                        if (!url) return null;
                        
                        return (
                          <a
                            key={platform}
                            href={url.startsWith('http') ? url : `https://${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-gray-500 ${color} transition-colors`}
                            title={label}
                          >
                            <Icon className="w-5 h-5" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>


          </CardContent>
        </Card>

        <MerchantStorefrontPhotoGallery
          images={galleryImages}
          businessName={displayBusinessName}
          loadingPhotos={isPollingPhotos}
        />
        <MerchantStorefrontCatalog merchant={merchant} />

        {/* Future: Product Catalog will go here */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Products & Services</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                Product catalog coming soon
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
};

export default MerchantPublicProfile;
